import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';
import {
    ACTIVE_STATUSES, Appointment, addDays, dayKey, endTimeOf, formatDay, interventionLabel, timeToMin, todayKey
} from './appointmentUtils';

const DAY_NAMES = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const HOUR_PX = 48;
const HATCH = { backgroundImage: 'repeating-linear-gradient(135deg, #f1f5f9 0, #f1f5f9 6px, #e2e8f0 6px, #e2e8f0 7px)' };

export interface DayConfig { open: string; close: string; lunchStart?: string | null; lunchEnd?: string | null; closed?: boolean }

interface Props {
    appointments: Appointment[];
    weekStart: string;                       // lundi YYYY-MM-DD
    schedule?: Record<string, DayConfig>;    // horaires du garage : zones fermées / pause hachurées
    closures?: Array<{ from: string; to: string; label?: string }>;
    summary?: { count: number; occupancyPercent: number } | null;
    onPrev: () => void;
    onNext: () => void;
    onToday: () => void;
    onSelect: (a: Appointment) => void;
}

interface Block { key: string; a: Appointment; date: string; timeSlot: string; duration: number; pending: boolean }

// Un RDV occupe 1 ou 2 blocs (changement demandé : l'ancien créneau + la proposition en pointillés)
const blocksOf = (a: Appointment): Block[] => {
    const cur: Block = { key: `${a._id}-cur`, a, date: dayKey(a.date), timeSlot: a.timeSlot, duration: a.duration, pending: a.status === 'REQUESTED' };
    if (a.status === 'COUNTER_PROPOSED' && a.pendingProposal) {
        return [{ key: `${a._id}-pp`, a, date: dayKey(a.pendingProposal.date), timeSlot: a.pendingProposal.timeSlot, duration: a.pendingProposal.duration || a.duration, pending: true }];
    }
    if (a.status === 'CHANGE_REQUESTED' && a.pendingProposal) {
        return [cur, { key: `${a._id}-pp`, a, date: dayKey(a.pendingProposal.date), timeSlot: a.pendingProposal.timeSlot, duration: a.pendingProposal.duration || a.duration, pending: true }];
    }
    return [cur];
};

export const WeekAgenda: React.FC<Props> = ({ appointments, weekStart, schedule, closures = [], summary, onPrev, onNext, onToday, onSelect }) => {
    const { t, currentLanguage, isRTL } = useLanguage();
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const today = todayKey();

    const cfgOf = (d: string): DayConfig | undefined => schedule?.[DAY_NAMES[new Date(`${d}T00:00:00Z`).getUTCDay()]];
    const closureOf = (d: string) => closures.find(c => c.from <= d && d <= c.to);
    const isClosed = (d: string) => !!closureOf(d) || (schedule ? !!cfgOf(d)?.closed : false);

    // Plage horaire affichée : du plus tôt au plus tard parmi les jours ouverts (8 h – 18 h par défaut)
    const opens = days.map(d => cfgOf(d)).filter((c): c is DayConfig => !!c && !c.closed);
    const startMin = Math.floor((opens.length ? Math.min(...opens.map(c => timeToMin(c.open))) : 8 * 60) / 60) * 60;
    const endMin = Math.ceil((opens.length ? Math.max(...opens.map(c => timeToMin(c.close))) : 18 * 60) / 60) * 60;
    const hours = Array.from({ length: Math.max(1, (endMin - startMin) / 60) }, (_, i) => startMin / 60 + i);
    const px = (min: number) => ((min - startMin) / 60) * HOUR_PX;
    const blocks = appointments.filter(a => ACTIVE_STATUSES.includes(a.status) || a.status === 'COMPLETED').flatMap(blocksOf);
    const Prev = isRTL ? ChevronRight : ChevronLeft;
    const Next = isRTL ? ChevronLeft : ChevronRight;

    return (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 sm:px-4 py-3 border-b border-gray-200">
                <div className="flex items-center gap-2 min-w-0">
                    <button onClick={onPrev} aria-label={t('appointments.agenda.prevWeek')} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><Prev size={16} /></button>
                    <button onClick={onNext} aria-label={t('appointments.agenda.nextWeek')} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><Next size={16} /></button>
                    <h3 className="font-bold text-gray-900 ms-1 text-sm sm:text-base truncate">
                        {formatDay(days[0], currentLanguage, { day: 'numeric' })} – {formatDay(days[6], currentLanguage, { day: 'numeric', month: 'long', year: 'numeric' })}
                    </h3>
                    <button onClick={onToday} className="hidden sm:inline-flex ms-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50">{t('appointments.agenda.today')}</button>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-indigo-600" />{t('appointments.agenda.legendConfirmed')}</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full border-2 border-dashed border-amber-500" />{t('appointments.agenda.legendPending')}</span>
                    {summary && <span className="font-semibold text-gray-800">{t('appointments.agenda.summary', { count: summary.count, percent: summary.occupancyPercent })}</span>}
                </div>
            </div>

            {/* Petit écran : liste des rendez-vous jour par jour */}
            <div className="md:hidden divide-y divide-gray-100">
                {days.map(d => {
                    const list = blocks.filter(b => b.date === d).sort((x, y) => x.timeSlot.localeCompare(y.timeSlot));
                    const closed = isClosed(d);
                    if (list.length === 0 && !closed) return null;
                    return (
                        <div key={d} className="px-3 py-3">
                            <p className={`text-sm font-bold mb-2 ${d === today ? 'text-indigo-600' : 'text-gray-900'}`}>
                                {formatDay(d, currentLanguage, { weekday: 'long', day: 'numeric', month: 'long' })}
                                {closed && <span className="ms-2 text-xs font-medium text-gray-400">{closureOf(d)?.label || t('appointments.settings.closed')}</span>}
                            </p>
                            <div className="space-y-2">
                                {list.map(b => (
                                    <button key={b.key} onClick={() => onSelect(b.a)}
                                            className={`w-full text-start rounded-xl border px-3 py-2 ${b.pending ? 'bg-amber-50 border-dashed border-amber-500 text-amber-900' : b.a.status === 'COMPLETED' ? 'bg-gray-50 border-gray-300 text-gray-600' : 'bg-indigo-50 border-indigo-200 text-indigo-900'}`}>
                                        <span className="block text-xs font-semibold">{b.timeSlot} – {endTimeOf({ timeSlot: b.timeSlot, duration: b.duration })}</span>
                                        <span className="block text-sm font-bold">{interventionLabel(b.a, t(`appointments.type.${b.a.type}`, { defaultValue: b.a.type }))}</span>
                                        <span className="block text-xs opacity-75">{b.a.clientName}{b.a.vehicleModel ? ` · ${b.a.vehicleModel}` : ''}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}
                {blocks.length === 0 && <p className="px-3 py-8 text-center text-sm text-gray-500">{t('appointments.agenda.empty')}</p>}
            </div>

            <div className="hidden md:block overflow-x-auto">
                <div className="min-w-[720px]">
                    <div className="flex border-b border-gray-200">
                        <div className="w-12 flex-shrink-0" />
                        {days.map(d => (
                            <div key={d} className={`flex-1 py-2 text-center ${d === today ? 'bg-indigo-50' : ''}`}>
                                <div className="text-xs font-medium text-gray-500">{formatDay(d, currentLanguage, { weekday: 'short' })}</div>
                                <div className={`text-xl font-extrabold ${d === today ? 'text-indigo-600' : 'text-gray-900'}`}>{Number(d.slice(8, 10))}</div>
                            </div>
                        ))}
                    </div>
                    <div className="flex">
                        <div className="w-12 flex-shrink-0">
                            {hours.map(h => (
                                <div key={h} style={{ height: HOUR_PX }} className="text-[11px] text-gray-400 text-end pe-2 relative -top-2">{String(h).padStart(2, '0')}:00</div>
                            ))}
                        </div>
                        {days.map(d => {
                            const c = cfgOf(d);
                            const closed = isClosed(d);
                            const lunch = !closed && c?.lunchStart && c?.lunchEnd ? [timeToMin(c.lunchStart), timeToMin(c.lunchEnd)] : null;
                            return (
                                <div key={d} className={`flex-1 relative border-s border-gray-100 ${d === today && !closed ? 'bg-indigo-50/30' : ''}`} style={{ height: HOUR_PX * hours.length, ...(closed ? HATCH : {}) }}>
                                    {hours.map((h, i) => <div key={h} style={{ top: i * HOUR_PX }} className="absolute inset-x-0 border-t border-gray-100" />)}
                                    {closed && <div className="absolute inset-x-0 top-1/3 text-center text-xs font-medium text-gray-500">{closureOf(d)?.label || t('appointments.settings.closed')}</div>}
                                    {lunch && <div className="absolute inset-x-0" style={{ top: px(lunch[0]), height: px(lunch[1]) - px(lunch[0]), ...HATCH }} />}
                                    {blocks.filter(b => b.date === d).map(b => {
                                        const top = px(timeToMin(b.timeSlot));
                                        const height = Math.max((b.duration / 60) * HOUR_PX - 2, 22);
                                        if (top < 0 || top >= HOUR_PX * hours.length) return null;
                                        const label = interventionLabel(b.a, t(`appointments.type.${b.a.type}`, { defaultValue: b.a.type }));
                                        return (
                                            <button
                                                key={b.key}
                                                onClick={() => onSelect(b.a)}
                                                style={{ top, height }}
                                                className={`absolute inset-x-1 rounded-lg px-1.5 py-1 text-start overflow-hidden text-[11px] leading-tight border transition-shadow hover:shadow-md ${
                                                    b.pending
                                                        ? 'bg-amber-50 border-dashed border-amber-500 text-amber-900'
                                                        : b.a.status === 'COMPLETED'
                                                            ? 'bg-gray-50 border-gray-300 text-gray-600'
                                                            : 'bg-indigo-50 border-indigo-200 text-indigo-900'
                                                }`}
                                            >
                                                <div className="font-semibold">{b.timeSlot} – {endTimeOf({ timeSlot: b.timeSlot, duration: b.duration })}</div>
                                                {height > 30 && <div className="truncate font-bold">{label}</div>}
                                                {height > 46 && <div className="truncate opacity-75">{b.a.clientName}{b.a.vehicleModel ? ` · ${b.a.vehicleModel}` : ''}</div>}
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
